import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import { CirculationGroupWithShiftAndStay } from '@wilson/interfaces';
import { CirculationGroupItemsSelectionHelper } from './circulation-group-items-selection.helper';
import { GroupItemsSelectionHelper } from './group-items-selection-helper.interface';
import { NoOpGroupItemsSelectionHelper } from './no-op-group-items-selection.helper';

@Injectable({
  providedIn: 'root',
})
export class GroupItemsSelectionHelperService
  implements GroupItemsSelectionHelper
{
  private readonly circulationGroupItemsSelectionHelper = inject(
    CirculationGroupItemsSelectionHelper,
  );
  private readonly noOpGroupItemsSelectionHelper = inject(
    NoOpGroupItemsSelectionHelper,
  );
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);

  private readonly isCirculationGroupingEnabled = toSignal(
    this.featureFlagPurePipe.transform('portal-circulation-grouping'),
    { initialValue: false },
  );

  public readonly groupedShiftIds = computed(() =>
    this.isCirculationGroupingEnabled()
      ? this.circulationGroupItemsSelectionHelper.groupedShiftIds()
      : this.noOpGroupItemsSelectionHelper.groupedShiftIds(),
  );

  public readonly groupedStayIds = computed(() =>
    this.isCirculationGroupingEnabled()
      ? this.circulationGroupItemsSelectionHelper.groupedStayIds()
      : this.noOpGroupItemsSelectionHelper.groupedStayIds(),
  );

  private get helper(): GroupItemsSelectionHelper {
    return this.isCirculationGroupingEnabled()
      ? this.circulationGroupItemsSelectionHelper
      : this.noOpGroupItemsSelectionHelper;
  }

  public selectGroupItems(elementId: string): Promise<void> {
    return this.helper.selectGroupItems(elementId);
  }

  public updateGroupItemsSelection(elementId: string): Promise<void> {
    return this.helper.updateGroupItemsSelection(elementId);
  }

  public syncGroupItemsFromElementIds(elementIds: string[]): Promise<void> {
    return this.helper.syncGroupItemsFromElementIds(elementIds);
  }

  public clearSelectedItems(): Promise<void> {
    return this.helper.clearSelectedItems();
  }

  public getCachedGroupItemsForElement(
    elementId: string,
  ): CirculationGroupWithShiftAndStay[] | null {
    if (!this.isCirculationGroupingEnabled()) {
      return null;
    }

    return this.circulationGroupItemsSelectionHelper.getCachedGroupItemsForElement(
      elementId,
    );
  }
}
